<script lang="ts" setup>
import { useRoute, useRouter } from "vue-router"
import { ThemeButton } from "~/components/theme"
import { Separator } from "~/components/ui/separator"
import { Sheet, SheetContent, SheetDescription, SheetTitle, SheetTrigger } from "~/components/ui/sheet"
import { useConcept } from "~/composables/concept"
import { useLocation } from "~/composables/locations"
import { scrollToAndFocus } from "~/composables/scroll"
import { titleCase } from "~/lib/case"
import { NAV_DEBOUNCE_MS, NavItem, NavLogo, NavMenu } from "."

const { items, side } = defineProps<{
    items: NavItem[]
    side?: "left" | "right"
}>()

const route = useRoute()
const router = useRouter()
const { concept } = useConcept()
const { location, hasOneLocation } = useLocation()
const hasConceptRoute = router.hasRoute("concept")

const open = ref(false)

const globalRoute = computed(() => {
    const globalRoutes = ["concept", "terms-of-use", "privacy-policy", "accessibility-statement"]
    return globalRoutes.includes(route.name as string)
})

let pendingSection: string | null = null

function handleSelect(sectionId?: string) {
    pendingSection = sectionId ?? null
    setTimeout(() => open.value = false, NAV_DEBOUNCE_MS)
}

function handleCloseAutoFocus(e: Event) {
    if (pendingSection) {
        e.preventDefault()
        scrollToAndFocus(pendingSection, 400)
        pendingSection = null
    }
}
</script>

<template>
    <Sheet
        v-if="location && route.name !== 'concept'"
        v-model:open="open"
    >
        <SheetTrigger as-child>
            <slot />
        </SheetTrigger>
        <SheetContent
            :side="side || 'left'"
            class="space-y-12 border-muted overflow-y-auto bg-nav-bar-sidebar-background text-nav-bar-sidebar-text"
            @closeAutoFocus="handleCloseAutoFocus"
        >
            <SheetTitle class="sr-only">
                Navigation
            </SheetTitle>
            <SheetDescription class="sr-only">
                Sidebar navigation menu
            </SheetDescription>
            <div class="space-y-6">
                <NavLogo 
                    :sidebar="true"
                    @select="handleSelect"
                />
                <template v-if="!globalRoute">
                    <NavMenu
                        variant="vertical"
                        :items="items"
                        @select="handleSelect"
                    />
                </template>
            </div>
            <template v-if="!globalRoute && !hasOneLocation">
                <Separator
                    v-if="hasConceptRoute && items.length > 0"
                    class="bg-muted"
                />
            </template>
            <div
                v-if="hasConceptRoute"
                class="space-y-6"
            >
                <h3 class="text-xs uppercase text-muted">
                    Locations
                </h3>
                <ul class="flex flex-col gap-4">
                    <li
                        v-for="l of concept.locations"
                        :key="l.name"
                        class="flex items-center"
                    >
                        <RouterLink
                            v-slot="{ navigate }"
                            :to="l.path"
                            rel="noopener noreferrer"
                            :aria-label="`Navigate to ${titleCase(concept.name)} ${titleCase(l.name)} page`"
                            tabindex="0"
                            custom
                            @click="open = false"
                        >
                            <ThemeButton
                                variant="link"
                                size="sm"
                                class="text-foreground uppercase px-0"
                                @click="navigate"
                            >
                                {{ titleCase(l.name) }}
                            </ThemeButton>
                        </RouterLink>
                    </li>
                    <li class="flex items-center">
                        <RouterLink
                            v-slot="{ navigate }"
                            :to="{ name: 'concept' }"
                            rel="noopener noreferrer"
                            :aria-label="`Navigate to all locations page`"
                            tabindex="0"
                            custom
                            @click="open = false"
                        >
                            <ThemeButton
                                variant="link"
                                size="sm"
                                class="text-foreground uppercase px-0"
                                @click="navigate"
                            >
                                All Locations
                            </ThemeButton>
                        </RouterLink>
                    </li>
                </ul>
            </div>
        </SheetContent>
    </Sheet>
</template>
